// components/ContentLibrary.tsx
// Content library view with filtering, sorting, and management

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, limit, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { BarkBoardPost, PostType } from '../types';
import { PostCard } from './PostCard';
import './ContentLibrary.css';

type SortOption = 'newest' | 'oldest' | 'most-views' | 'most-compliments';
type FilterOption = 'all' | 'video' | 'audio' | 'article';

export const ContentLibrary: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();

    const [posts, setPosts] = useState<BarkBoardPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState<FilterOption>('all');
    const [sortBy, setSortBy] = useState<SortOption>('newest');
    const [searchTerm, setSearchTerm] = useState('');
    const [isDeleting, setIsDeleting] = useState<string | null>(null);

    // Load posts from Firebase
    const loadPosts = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            // Query without where filters to avoid index requirements
            // Filter client-side instead
            let q = query(
                collection(db, 'barkBoard', 'posts', 'items'),
                orderBy('publishedAt', 'desc'),
                limit(100) // Increased limit to get more posts before client filtering
            );

            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                setPosts([]);
                setLoading(false);
                return;
            }

            let loadedPosts = snapshot.docs
                .map(doc => {
                    const data = doc.data();

                    // Skip if not admin post or not active (client-side filter)
                    if (data.postType !== 'admin' || data.isActive !== true) {
                        return null;
                    }

                    // More flexible type mapping
                    let postType: PostType = 'video';
                    if (data.type === 'audio' || data.type === 'adminAudio') {
                        postType = 'audio';
                    } else if (data.type === 'article' || data.type === 'adminArticle') {
                        postType = 'article';
                    } else if (data.type === 'video' || data.type === 'adminVideo') {
                        postType = 'video';
                    }

                    return {
                        id: doc.id,
                        type: postType,
                        title: data.title || 'Untitled',
                        caption: data.caption || data.articleContent?.substring(0, 200) || '',
                        category: data.category,
                        videoUrl: data.videoUrl || data.mediaURL,
                        audioUrl: data.audioUrl || data.mediaURL,
                        thumbnailUrl: data.thumbnailUrl || data.thumbnailURL,
                        articleContent: data.articleContent,
                        authorName: data.authorName || 'Omore Team',
                        authorEmail: data.authorEmail,
                        createdAt: data.createdAt?.toDate() || data.publishedAt?.toDate() || new Date(),
                        publishedAt: data.publishedAt?.toDate() || data.createdAt?.toDate() || new Date(),
                        status: data.status || 'published',
                        viewCount: data.viewCount || 0,
                        complimentCount: data.complimentCount || 0,
                    } as BarkBoardPost;
                })
                .filter(post => post !== null) as BarkBoardPost[];

            // Apply content type filter
            if (filter !== 'all') {
                loadedPosts = loadedPosts.filter(post => post.type === filter);
            }

            // Apply sorting (already sorted by publishedAt desc from query)
            // Only re-sort if user changed sort option
            if (sortBy === 'oldest') {
                loadedPosts.reverse();
            } else if (sortBy === 'most-views') {
                loadedPosts.sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));
            } else if (sortBy === 'most-compliments') {
                loadedPosts.sort((a, b) => (b.complimentCount || 0) - (a.complimentCount || 0));
            }

            setPosts(loadedPosts);
        } catch (err: any) {
            console.error('Failed to load posts:', err);
            setError('Failed to load content. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [sortBy, filter]);

    useEffect(() => {
        loadPosts();
    }, [loadPosts]);

    const handleDelete = async (postId: string) => {
        if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
            return;
        }

        try {
            setIsDeleting(postId);

            // Soft delete - set isActive to false
            await updateDoc(doc(db, 'barkBoard', 'posts', 'items', postId), {
                isActive: false,
                deletedAt: new Date(),
                deletedBy: currentUser?.email
            });

            // Remove from local state
            setPosts(posts.filter(post => post.id !== postId));

            alert('Post deleted successfully');
        } catch (err: any) {
            console.error('Delete error:', err);
            alert('Failed to delete post. Please try again.');
        } finally {
            setIsDeleting(null);
        }
    };

    const handleEdit = (post: BarkBoardPost) => {
        // Navigate to the appropriate edit page based on post type
        navigate(`/edit-${post.type}/${post.id}`);
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    // Filter posts by search term
    const filteredPosts = posts.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.caption.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const stats = {
        total: posts.length,
        videos: posts.filter(p => p.type === 'video').length,
        audio: posts.filter(p => p.type === 'audio').length,
        articles: posts.filter(p => p.type === 'article').length,
        totalViews: posts.reduce((sum, p) => sum + (p.viewCount || 0), 0),
        totalCompliments: posts.reduce((sum, p) => sum + (p.complimentCount || 0), 0),
    };

    return (
        <div className="library-container">
            {/* Header */}
            <header className="library-header">
                <div className="header-content">
                    <div className="header-left">
                        <button onClick={() => navigate('/dashboard')} className="back-button">
                            ← Back
                        </button>
                        <h1>Content Library</h1>
                    </div>
                    <div className="header-actions">
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <div className="library-content">
                {/* Stats Overview */}
                <div className="stats-overview">
                    <div className="stat-item">
                        <span className="stat-value">{stats.total}</span>
                        <span className="stat-label">Total Posts</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{stats.videos}</span>
                        <span className="stat-label">Videos</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{stats.audio}</span>
                        <span className="stat-label">Audio</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{stats.articles}</span>
                        <span className="stat-label">Articles</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{stats.totalViews}</span>
                        <span className="stat-label">Total Views</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{stats.totalCompliments}</span>
                        <span className="stat-label">Compliments</span>
                    </div>
                </div>

                {/* Controls */}
                <div className="library-controls">
                    <div className="search-box">
                        <input
                            type="text"
                            placeholder="Search by title or caption..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <div className="filter-controls">
                        <div className="filter-group">
                            <label>Filter:</label>
                            <select
                                value={filter}
                                onChange={(e) => setFilter(e.target.value as FilterOption)}
                                className="filter-select"
                            >
                                <option value="all">All Types</option>
                                <option value="video">Videos</option>
                                <option value="audio">Audio</option>
                                <option value="article">Articles</option>
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Sort by:</label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as SortOption)}
                                className="filter-select"
                            >
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="most-views">Most Views</option>
                                <option value="most-compliments">Most Compliments</option>
                            </select>
                        </div>

                        <button onClick={loadPosts} className="refresh-button">
                            🔄 Refresh
                        </button>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                {/* Loading State */}
                {loading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading content...</p>
                    </div>
                ) : filteredPosts.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">🔭</div>
                        <h3>No content found</h3>
                        <p>
                            {searchTerm
                                ? `No posts match "${searchTerm}"`
                                : 'Start creating content to see it here'
                            }
                        </p>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="create-button"
                        >
                            Create Content
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Results Count */}
                        <div className="results-info">
                            Showing {filteredPosts.length} {filteredPosts.length === 1 ? 'post' : 'posts'}
                        </div>

                        {/* Posts Grid */}
                        <div className="posts-grid">
                            {filteredPosts.map(post => (
                                <PostCard
                                    key={post.id}
                                    post={post}
                                    onDelete={handleDelete}
                                    onEdit={handleEdit}
                                    isDeleting={isDeleting === post.id}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};